import React, { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ProfileProvider } from "./contexts/ProfileContext";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { BadgesProvider } from "./contexts/BadgesContext";
import { CIStatusBanner } from "./components/CIStatusBanner";

// All components loaded eagerly for single bundle (no lazy loading)
import ProtectedMainLayout from "./components/auth/ProtectedMainLayout";
import ProtectedOnboardingLayout from "./components/auth/ProtectedOnboardingLayout";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Feed from "./pages/main/Feed";
import ProfileEdit from "./pages/main/ProfileEdit";
import ChatListPage from "./pages/main/ChatListPage";
import Connections from "./pages/main/Connections";
import Missions from "./pages/main/Missions";
import WheelPage from "./pages/main/WheelPage";
import Subscriptions from "./pages/main/Subscriptions";
import UserProfile from "./pages/main/UserProfile";
import ChatConversation from "./pages/main/ChatConversation";
import About from "./pages/About";
import EarlyAccess from "./pages/EarlyAccess";
import Onboarding from "./pages/main/Onboarding";
import CompleteProfile from "./pages/main/CompleteProfile";
import Verification from "./pages/main/Verification";
import Notifications from "./pages/main/Notifications";
import EmailPreferences from "./pages/main/EmailPreferences";
import AdminPanel from "./pages/main/AdminPanel";
import Support from "./pages/main/Support";
import SupportTicketDetails from "./pages/main/SupportTicketDetails";
import ErrorPage from "./pages/main/ErrorPage";
import LoginPage from "./pages/auth/LoginPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import VerifyTokenPage from "./pages/auth/VerifyTokenPage";
import ReactivateAccount from "./pages/auth/ReactivateAccount";
import Logout from "./pages/main/Logout";
import DatadogMetrics from "./pages/observability/DatadogMetrics";
import Unsubscribe from "./pages/Unsubscribe";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import SupportPublic from "./pages/SupportPublic";

// Minimal loading fallback (kept for any edge cases)
const PageLoader = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-black">
    <div className="relative w-16 h-16 mb-4">
      <div className="absolute inset-0 rounded-full border-4 border-luxury-red/20"></div>
      <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-luxury-red border-r-luxury-mediumPink animate-spin"></div>
    </div>
    <div className="text-white/60 text-sm font-light">Carregando...</div>
  </div>
);

const App = () => {
  return (
    <ProfileProvider>
      <NotificationsProvider>
        <BadgesProvider>
          <CIStatusBanner />
          <Suspense fallback={<PageLoader />}>
            <Routes>
        {/* Landing page */}
        <Route path="/" element={<Index />} />
      <Route path="/home" element={<Navigate to="/main/feed" replace />} />

      {/* Auth pages */}
      <Route path="/auth" element={<LoginPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/verify" element={<VerifyTokenPage />} />
      <Route path="/reactivate-account" element={<ReactivateAccount />} />

      {/* Public routes */}
      <Route path="/about" element={<About />} />
      <Route path="/acesso-antecipado" element={<EarlyAccess />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/support" element={<SupportPublic />} />
      <Route path="/main/error" element={<ErrorPage />} />

      {/* Observability routes */}
      <Route path="/observability/datadog/metrics" element={<DatadogMetrics />} />

      {/* Logout route */}
      <Route path="/main/logout" element={<Logout />} />

      {/* Onboarding route with its own protected layout */}
      <Route path="/main/onboarding" element={<ProtectedOnboardingLayout />}>
        <Route index element={<Onboarding />} />
      </Route>

      {/* Main routes with protected layout */}
      <Route path="/main" element={<ProtectedMainLayout />}>
        <Route path="feed" element={<Feed />} />
        <Route path="user/:username" element={<UserProfile />} />
        <Route path="profile" element={<ProfileEdit />} />
        <Route path="complete-profile" element={<CompleteProfile />} />
        <Route path="chats" element={<ChatListPage />} />
        <Route path="chat/:username" element={<ChatConversation />} />
        <Route path="connections" element={<Connections />} />
        <Route path="missions" element={<Missions />} />
        <Route path="wheel" element={<WheelPage />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="support" element={<Support />} />
        <Route path="support/:ticketId" element={<SupportTicketDetails />} />
        <Route path="verify" element={<Verification />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="email-preferences" element={<EmailPreferences />} />
        <Route path="joia/admin/*" element={<AdminPanel />} />
      </Route>

        {/* Catch-all route */}
        <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BadgesProvider>
      </NotificationsProvider>
    </ProfileProvider>
  );
};

export default App;
